import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Stethoscope, 
  Heart, 
  Baby, 
  Eye, 
  Ear, 
  Activity,
  UserCircle,
  Calendar,
  Award,
  Phone,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for doctor specializations
const getSpecializationIcon = (iconType, specialty) => {
  const iconClass = "w-16 h-16 text-primary";
  
  // First check icon_type from database
  switch (iconType) {
    case 'dental':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C7.5 2 6 5 6 8c0 2 .5 3 1 4.5.5 1.5 1 3 1 5.5 0 2 1 4 2.5 4s2-1.5 2.5-3.5c.5 2 1 3.5 2.5 3.5s2.5-2 2.5-4c0-2.5.5-4 1-5.5S18 10 18 8c0-3-1.5-6-6-6z"/>
        </svg>
      );
    case 'eye':
      return <Eye className={iconClass} />;
    case 'maternal':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="5"/>
          <path d="M12 13c-4 0-7 2-7 5v2h14v-2c0-3-3-5-7-5z"/>
          <circle cx="12" cy="8" r="2"/>
        </svg>
      );
    case 'pediatric':
      return <Baby className={iconClass} />;
    case 'physiotherapy':
      return <Activity className={iconClass} />;
    case 'ent':
      return <Ear className={iconClass} />;
    case 'stethoscope':
    default:
      return <Stethoscope className={iconClass} />;
  }
};

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${API}/doctors`);
        if (response.data && response.data.length > 0) {
          setDoctors(response.data);
        }
      } catch (error) {
        console.error('Error fetching doctors:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDoctors();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary to-primary-700 py-20 md:py-28" data-testid="doctors-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-primary-100 font-semibold mb-3 tracking-wide uppercase text-sm">Our Medical Team</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Meet Our Expert Doctors
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Our team of experienced healthcare professionals is dedicated to providing you with the highest quality medical care. Get to know the experts who will be taking care of your health at Baobab Hospital.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-slate-50 border-b" data-testid="doctors-stats">
        <div className="container-custom">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="font-heading text-3xl font-bold text-primary">17</p>
              <p className="text-slate-600 text-sm">Medical Professionals</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-bold text-primary">8+</p>
              <p className="text-slate-600 text-sm">Specialties</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-bold text-primary">2+</p>
              <p className="text-slate-600 text-sm">Years of Service</p>
            </div>
          </div>
        </div>
      </section>

      {/* Doctors Grid */}
      <section className="py-16 md:py-24" data-testid="doctors-grid">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold mb-2 tracking-wide uppercase text-sm">Healthcare Professionals</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Our Doctors
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Click on any doctor's profile to learn more about their qualifications and book an appointment.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-white rounded-2xl p-6 shadow-sm border">
                    <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-3/4 mx-auto mb-2" />
                    <div className="h-5 bg-slate-200 rounded w-2/3 mx-auto" />
                  </div>
                </div>
              ))}
            </div>
          ) : doctors.length === 0 ? (
            <div className="text-center py-12">
              <UserCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No doctors found. Please check back later.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {doctors.map((doctor, index) => (
                <Link to={`/doctors/${doctor.id}`} key={doctor.id} data-testid={`doctor-card-${index}`}>
                  <Card 
                    className="bg-white rounded-2xl overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-primary/20 h-full"
                  >
                    <CardContent className="p-6 text-center">
                      {/* Icon or Photo */}
                      <div className="mb-4">
                        {doctor.image ? (
                          <div className="w-20 h-20 mx-auto rounded-full overflow-hidden border-4 border-primary/10">
                            <img
                              src={doctor.image}
                              alt={doctor.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center group-hover:from-primary/20 group-hover:to-primary/10 transition-colors duration-300">
                            {getSpecializationIcon(doctor.icon_type, doctor.specialty)}
                          </div>
                        )}
                      </div>
                      
                      {/* Qualification Badge */}
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                          {doctor.specialty_code || doctor.title || 'Medical Professional'}
                        </span>
                      </div>
                      
                      {/* Doctor Name */}
                      <h3 className="font-heading font-bold text-slate-800 text-lg mb-1 group-hover:text-primary transition-colors">
                        {doctor.name}
                      </h3>
                      
                      {/* Specialty */}
                      <p className="text-slate-500 text-sm">
                        {doctor.specialty || doctor.title}
                      </p>
                      
                      {/* View Profile Link */}
                      <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-primary text-sm font-medium flex items-center justify-center gap-1">
                          View Profile <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary-700" data-testid="doctors-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Book an Appointment?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Our medical team is ready to provide you with excellent healthcare. Book your appointment today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-white text-primary hover:bg-slate-100 rounded-lg px-8">
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </Link>
            <a href="tel:+255659984488">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
